/**
 * WordPress dependencies
 */
import { BaseControl, HStack, VStack } from '@safe-wordpress/components';
import { useState } from '@safe-wordpress/element';
import { _x, sprintf } from '@safe-wordpress/i18n';

/**
 * Internal dependencies
 */
import useSpacingSizes from './hooks/use-spacing-sizes';
import AxialInputControls from './input-controls/axial';
import SeparatedInputControls from './input-controls/separated';
import SingleInputControl from './input-controls/single';
import LinkedButton from './linked-button';
import {
	ALL_SIDES,
	DEFAULT_VALUES,
	LABELS,
	VIEWS,
	getInitialView,
} from './utils';

export {
	getCustomValueFromPreset,
	getPresetValueFromCustomValue,
} from './utils';

/**
 * A flexible control for managing spacing values in the block editor. Supports single, axial,
 * and separated input controls for different spacing configurations with automatic view selection
 * based on current values and available sides.
 *
 * @see https://github.com/WordPress/gutenberg/blob/HEAD/packages/block-editor/src/components/spacing-sizes-control/README.md
 *
 * @example
 * ```jsx
 * import { __experimentalSpacingSizesControl as SpacingSizesControl } from '@safe-wordpress/block-editor';
 * import { useState } from '@safe-wordpress/element';
 *
 * function Example() {
 *   const [ sides, setSides ] = useState( {
 *     top: '0px',
 *     right: '0px',
 *     bottom: '0px',
 *     left: '0px',
 *   } );
 *
 *   return (
 *     <SpacingSizesControl
 *       values={ sides }
 *       onChange={ setSides }
 *       label="Sides"
 *     />
 *   );
 * }
 * ```
 *
 * @param {Object}   props                    Component props.
 * @param {Object}   props.inputProps         Additional props for input controls.
 * @param {string}   props.label              Label for the control.
 * @param {number}   props.minimumCustomValue Minimum value for custom input.
 * @param {Function} props.onChange           Called when spacing values change.
 * @param {Function} props.onMouseOut         Called when mouse leaves the control.
 * @param {Function} props.onMouseOver        Called when mouse enters the control.
 * @param {boolean}  props.showSideInLabel    Show side in control label.
 * @param {Array}    props.sides              Available sides for control.
 * @param {boolean}  props.useSelect          Use select control for predefined values.
 * @param {Object}   props.values             Current spacing values.
 * @return {Element}                         Spacing sizes control component.
 */
type SpacingSizesControlProps = {
	readonly label: string;
	readonly minimumCustomValue?: number;
	readonly onChange: ( values: Record< string, string | undefined > ) => void;
	readonly onMouseOut?: ( event: React.SyntheticEvent ) => void;
	readonly onMouseOver?: ( event: React.SyntheticEvent ) => void;
	readonly showSideInLabel?: boolean;
	readonly sides?: ReadonlyArray< string >;
	readonly values?: Partial<
		Record< 'top' | 'right' | 'bottom' | 'left', string >
	>;
	readonly disabledSides: Record<
		'top' | 'right' | 'bottom' | 'left',
		boolean
	>;
};
export function SpacingSizesControl( {
	label: labelProp,
	minimumCustomValue = 0,
	onChange,
	onMouseOut,
	onMouseOver,
	showSideInLabel = true,
	sides = ALL_SIDES,
	values,
	disabledSides,
}: SpacingSizesControlProps ): JSX.Element {
	const spacingSizes = useSpacingSizes();
	const inputValues = { ...DEFAULT_VALUES, ...values };
	const hasOneSide = sides?.length === 1;
	const hasOnlyAxialSides =
		sides?.includes( 'horizontal' ) &&
		sides?.includes( 'vertical' ) &&
		sides?.length === 2;

	const initialView = getInitialView( inputValues, sides );
	const hasAsymmetricDisabledSide =
		disabledSides.top !== disabledSides.bottom ||
		disabledSides.left !== disabledSides.right;
	const [ view, setView ] = useState(
		initialView === VIEWS.axial && hasAsymmetricDisabledSide
			? VIEWS.custom
			: initialView
	);

	const toggleLinked = () => {
		setView( view === VIEWS.axial ? VIEWS.custom : VIEWS.axial );
	};

	const handleOnChange = (
		nextValue: Record< string, string | undefined >
	) => {
		const newValues = { ...values, ...nextValue };
		onChange( newValues );
	};

	const renderControls = () => {
		if ( view === VIEWS.axial ) {
			return (
				<AxialInputControls
					minimumCustomValue={ minimumCustomValue }
					onChange={ handleOnChange }
					onMouseOut={ onMouseOut }
					onMouseOver={ onMouseOver }
					sides={ sides }
					spacingSizes={ spacingSizes }
					type={ labelProp }
					values={ inputValues }
					disabledSides={ {
						horizontal: disabledSides.left,
						vertical: disabledSides.top,
					} }
				/>
			);
		}
		if ( view === VIEWS.custom ) {
			return (
				<SeparatedInputControls
					minimumCustomValue={ minimumCustomValue }
					onChange={ handleOnChange }
					onMouseOut={ onMouseOut }
					onMouseOver={ onMouseOver }
					sides={ sides }
					spacingSizes={ spacingSizes }
					type={ labelProp }
					values={ inputValues }
					disabledSides={ disabledSides }
				/>
			);
		}
		return (
			<SingleInputControl
				side={ view }
				minimumCustomValue={ minimumCustomValue }
				onChange={ handleOnChange }
				onMouseOut={ onMouseOut }
				onMouseOver={ onMouseOver }
				spacingSizes={ spacingSizes }
				type={ labelProp }
				values={ inputValues }
				disabledSides={ disabledSides }
				showSideInLabel={ showSideInLabel }
			/>
		);
	};

	const sideLabel =
		( ALL_SIDES as ReadonlyArray< string > ).includes( view ) &&
		showSideInLabel
			? LABELS[ view ]
			: '';

	const label = sprintf(
		/* translators: %1$s: The side of the block being modified (top, bottom, left etc.). %2$s: Type of spacing being modified (padding, margin, etc). */
		_x( '%1$s %2$s', 'spacing' ),
		labelProp,
		sideLabel
	).trim();

	return (
		<fieldset className="spacing-sizes-control">
			<HStack className="spacing-sizes-control__header">
				<BaseControl.VisualLabel
					as="legend"
					className="spacing-sizes-control__label"
				>
					{ label }
				</BaseControl.VisualLabel>
				{ ! hasAsymmetricDisabledSide &&
					! hasOneSide &&
					! hasOnlyAxialSides && (
						<LinkedButton
							label={ labelProp }
							onClick={ toggleLinked }
							isLinked={ view === VIEWS.axial }
						/>
					) }
			</HStack>
			<VStack spacing={ 0.5 }>{ renderControls() }</VStack>
		</fieldset>
	);
}

export { useSpacingSizes };
